import {useCallback, useMemo} from 'react';
import clsx from 'clsx';
import castArray from 'lodash/castArray';
import isEqual from 'lodash/isEqual';

import {useDocumentContext} from '@/components/document/context';
import Checkbox from '@/components/form/checkbox';
import Radio from '@/components/form/radio';
import {DocumentInputField, Option} from '@/engine/types';
import {DomainValueReference, FIELD_TYPES} from '@/model/types';
import {FieldOption, useFieldOptions} from '../../hooks/use-field-options';
import {FieldProps} from '../../meta-field';

const getOptionRef = (option: Option['value']) => {
  if (typeof option === 'string') return option;
  if ('$ref' in option) return option.$ref;
  if ('$template' in option) {
    if (typeof option.$template === 'string') {
      return option.$template;
    } else {
      return option.$template.en;
    }
  }
  return option.en;
};

export const ChoiceField = ({
  field,
  storedValue,
  handleChange,
  handleBlur,
  handleFocus,
}: FieldProps<DocumentInputField>) => {
  const options = useFieldOptions(field);

  const changeHandler = useCallback(
    (option: Option['value'], previouslyChecked: boolean) => {
      return () => {
        if (field.readonly) return;

        if (field.type === FIELD_TYPES.RADIO) {
          handleChange(option, true);
          return;
        }

        if (field.type === FIELD_TYPES.CHECKBOX) {
          if (field.multiple) {
            const checkedOptions = (storedValue as DomainValueReference[]) ?? [];

            const newValue = options.reduce(
              (acc, o) => {
                // If the option is the one we are toggling,
                // we add it to the new value if it was not previously checked,
                // or we remove it if it was previously checked
                if (o.raw === option) {
                  return !previouslyChecked ? [...acc, o.raw] : acc;
                }

                // If the option was already checked, we keep it in the new value
                if (checkedOptions.findIndex(checkedOption => isEqual(checkedOption, o.raw)) !== -1) {
                  return [...acc, o.raw];
                }
                return acc;
              },
              [] as FieldOption['raw'][],
            );

            handleChange(newValue, true);
          } else {
            handleChange(previouslyChecked ? '' : option, true);
          }
        }
      };
    },
    [field, handleChange, storedValue, options],
  );

  return (
    <div
      className={clsx('flex gap-4 py-4', (field.options?.length ?? 0) > 3 ? 'flex-col' : 'flex-row')}
      data-testid={`${field.id}-control`}
    >
      {options?.map(option => (
        <ChoiceOption
          data-testid={`${field.id}-control_option-${option.value}`}
          key={option.id}
          field={field}
          option={option}
          storedValue={storedValue}
          changeHandler={changeHandler}
          handleFocus={handleFocus}
          handleBlur={handleBlur}
        />
      ))}
    </div>
  );
};

type ChoiceOptionProps = Pick<
  FieldProps<DocumentInputField>,
  'field' | 'storedValue' | 'handleFocus' | 'handleBlur'
> & {
  option: FieldOption;
  changeHandler: (option: Option['value'], previouslyChecked: boolean) => () => void;
};

const ChoiceOption = ({field, option, storedValue, changeHandler, handleFocus, handleBlur}: ChoiceOptionProps) => {
  const {valueResolver} = useDocumentContext();

  const Choice = field.type === FIELD_TYPES.CHECKBOX ? Checkbox : Radio;
  const checked = useMemo(() => {
    return !!castArray(storedValue).find(checkedOption => {
      return isEqual(valueResolver(checkedOption), valueResolver(option.raw));
    });
  }, [storedValue, option.raw, valueResolver]);

  return (
    <Choice
      checked={checked}
      data-testid={`${field.id}-control_option-${option.value}`}
      key={option.label}
      label={option.label}
      multiple={field.multiple}
      name={field.id}
      readOnly={field.readonly}
      type={field.type}
      value={option.value}
      onChange={changeHandler(option.raw, checked)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-testvalue={getOptionRef(option.raw)}
    />
  );
};
